// Tasks table component

import { useMemo, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { Badge } from '@chakra-ui/react';
import { DataTable } from '@/design-system/components/DataTable';
import { TaskGroup } from '@/lib/sdk/chatbot/types';

const editedIndicatorColumn: ColumnDef<any> = {
  id: '__edited',
  header: '',
  accessorFn: (row) => Boolean(row.is_edited),
  size: 90,
  enableSorting: false,
  cell: (info) => {
    const isEdited = info.getValue() as boolean;
    if (!isEdited) return null;
    return (
      <Badge colorScheme="yellow" fontSize="xs" variant="subtle">
        Edited
      </Badge>
    );
  },
};

interface TasksTableProps {
  dynamicColumns: ColumnDef<any>[];
  taskGroups: TaskGroup[];
  canEdit?: boolean;
}

/**
 * Table displaying task groups with click-to-view/edit functionality
 * - If the user has edit permission: clicking row opens edit view
 * - Otherwise: clicking row opens read-only view
 * This eliminates the need to scroll right to find action buttons
 */
export function TasksTable({dynamicColumns, taskGroups, canEdit = false }: TasksTableProps) {
  const navigate = useNavigate();
  const { botId, orgId } = useParams<{ botId: string; orgId: string }>();
  const [searchParams] = useSearchParams();

  const canEditTasks = canEdit;

  // Handle row click - navigate to edit view if allowed, otherwise view
  // Preserves date range query params for back navigation
  const handleRowClick = useCallback((group: TaskGroup) => {
    const view = canEditTasks ? 'edit' : 'view';
    const queryString = searchParams.toString();
    const url = `/dashboard/${orgId}/workflows/${botId}/data/${group.source_key}/${view}${queryString ? `?${queryString}` : ''}`;
    navigate(url);
  }, [navigate, orgId, botId, canEditTasks, searchParams]);

  // Remove the Actions column since rows are now clickable
  const columns = useMemo<ColumnDef<any>[]>(
    () => [editedIndicatorColumn, ...dynamicColumns],
    [dynamicColumns]
  );

  return (
    <DataTable
      data={taskGroups}
      columns={columns}
      enableSorting
      enableFiltering
      enablePagination
      pageSize={20}
      emptyMessage="No data found for this date range"
      enableColumnVisibility={true}
      initialColumnVisibility={{ id: false }}
      onRowClick={handleRowClick}
    />
  );
}
